import { useState, useRef, useEffect, useCallback } from 'react'
import { TDrawingTool, ImageDimensions } from '../types'
import { useInpaintAndErase } from '../inpaint_and_erase/useInpaintAndErase'

interface Options {
	imageSrc: string
	onAccept?: (result: { url: string; width: number; height: number; cleanup: () => void }) => void
	onError?: (error: Error) => void
	onMaskChange?: (blob: Blob) => void
	dimensionId?: string
}

// Define a new interface that extends the result with dimensionId
interface ImageResultWithDimension {
	url: string
	width: number
	height: number
	cleanup: () => void
	dimensionId?: string
}

interface UseImageEditorReturn {
	imageCanvasRef: React.RefObject<HTMLCanvasElement>
	maskCanvasRef: React.RefObject<HTMLCanvasElement>
	currentTool: TDrawingTool
	brushSize: number
	isProcessing: boolean
	loadingMessage: string
	hasMask: boolean
	prompt: string
	showPromptInput: boolean
	showInputAbove: boolean
	setCurrentTool: (tool: TDrawingTool) => void
	setBrushSize: (size: number) => void
	setPrompt: (prompt: string) => void
	startDrawing: (e: React.MouseEvent) => void
	stopDrawing: () => void
	draw: (e: React.MouseEvent) => void
	undo: () => void
	clearMask: () => void
	handleSubmit: () => Promise<void>
	imageWidth: number
	imageHeight: number
	handleDimensionsChange: (width: number, height: number) => void
	setDimensions: (width: number, height: number) => void
	currentDimensionId?: string
}

/**
 * Main hook for AI Image Editor that combines all business logic
 */
export const useImageEditor = ({
	imageSrc,
	onAccept,
	onError,
	onMaskChange,
	dimensionId
}: Options): UseImageEditorReturn => {
	// Core refs
	const imageCanvasRef = useRef<HTMLCanvasElement>(null)
	const maskCanvasRef = useRef<HTMLCanvasElement>(null)

	// Image dimensions state
	const [imageWidth, setImageWidth] = useState(0)
	const [imageHeight, setImageHeight] = useState(0)
	const [originalDimensions, setOriginalDimensions] = useState<ImageDimensions | null>(null)

	// Create a wrapper for onAccept that includes the dimensionId
	const handleAccept = useCallback((result: { url: string; width: number; height: number; cleanup: () => void }) => {
		// Pass the dimensionId along with the result to the parent component
		if (onAccept) {
			const resultWithDimension: ImageResultWithDimension = {
				...result,
				dimensionId // Include the current dimension ID
			}
			onAccept(resultWithDimension as { url: string; width: number; height: number; cleanup: () => void })
		}
	}, [onAccept, dimensionId])

	// Get inpainting and erasing functionality
	const {
		currentTool,
		brushSize,
		isProcessing,
		hasMask,
		prompt,
		showPromptInput,
		showInputAbove,
		setCurrentTool,
		setBrushSize,
		setPrompt,
		startDrawing,
		stopDrawing,
		draw,
		undo,
		clearMask,
		handleSubmit,
	} = useInpaintAndErase({
		imageCanvasRef,
		maskCanvasRef,
		onAccept: handleAccept, // Use our wrapper instead of the original onAccept
		onError,
		onMaskChange,
	})

	/**
	 * Canvas Initialization
	 */
	useEffect(() => {
		const initializeCanvas = async () => {
			const imageCanvas = imageCanvasRef.current
			const maskCanvas = maskCanvasRef.current
			if (!imageCanvas || !maskCanvas) {
				return
			}

			// Load and draw the image first to get dimensions
			const image = new Image()

			await new Promise<void>((resolve) => {
				image.onload = () => {
					// Ensure image has valid dimensions
					if (image.width === 0 || image.height === 0) {
						resolve()
						return
					}

					// Set canvas dimensions to match original image size
					imageCanvas.width = image.width
					imageCanvas.height = image.height
					maskCanvas.width = image.width
					maskCanvas.height = image.height

					// Set actual image dimensions and store original
					setImageWidth(image.width)
					setImageHeight(image.height)
					setOriginalDimensions({ width: image.width, height: image.height })

					// Draw image at full size
					const ctx = imageCanvas.getContext('2d')
					if (!ctx) {
						resolve()
						return
					}

					// Clear canvas
					ctx.clearRect(0, 0, image.width, image.height)

					// Draw image at full size
					ctx.drawImage(image, 0, 0, image.width, image.height)
					resolve()
				}

				image.onerror = () => {
					resolve()
				}

				image.src = imageSrc
			})

			// Initialize mask canvas - just needs to be empty
			const maskCtx = maskCanvas.getContext('2d')
			if (maskCtx) {
				maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height)
			}
		}

		void initializeCanvas()
	}, [imageSrc])

	// Keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
				e.preventDefault()
				undo()
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [undo])

	// Handle dimension changes
	const handleDimensionsChange = useCallback(
		(newWidth: number, newHeight: number) => {
			// Don't process if dimensions haven't actually changed
			if (newWidth === imageWidth && newHeight === imageHeight) {
				return
			}

			// Calculate the differences from original dimensions
			if (!originalDimensions) {
				return
			}

			// Update dimensions state
			setImageWidth(newWidth)
			setImageHeight(newHeight)
		},
		[imageWidth, imageHeight, originalDimensions],
	)

	// Add direct dimension setter
	const setDimensions = useCallback((newWidth: number, newHeight: number) => {
		setImageWidth(newWidth)
		setImageHeight(newHeight)
	}, [])

	return {
		imageCanvasRef,
		maskCanvasRef,
		currentTool,
		brushSize,
		isProcessing,
		loadingMessage: '', // This can be removed from the interface later as it's handled by LoadingOverlay
		hasMask,
		prompt,
		showPromptInput,
		showInputAbove,
		setCurrentTool,
		setBrushSize,
		setPrompt,
		startDrawing,
		stopDrawing,
		draw,
		undo,
		clearMask,
		handleSubmit,
		imageWidth,
		imageHeight,
		handleDimensionsChange,
		setDimensions,
		currentDimensionId: dimensionId
	}
}
